import React, { useEffect } from 'react';
import { useMeasure, useToggle } from 'react-use';
import { theme } from '@noths/polaris-client-ribbons-base';
import { TextIcon } from '@noths/polaris-client-ribbons-design-system';
import type { CSSProp } from 'styled-components';

import chevronUp from 'src/assets/svg/chevronUp.svg';
import * as defaultStyles from '../styles';

export interface AccordionProps {
  title: string;
  id: string;
  children?: React.ReactNode;
  open?: boolean;
  buttonStyles?: CSSProp;
  titleStyles?: CSSProp;
}

export const Accordion = ({
  id,
  title,
  titleStyles,
  buttonStyles,
  children,
  open = false,
}: AccordionProps) => {
  const [isOpen, toggleOpen] = useToggle(open);
  const buttonId = `accordion_${id}__button`;
  const sectionId = `accordion_${id}__section`;
  const [contentRef, contentDimensions] = useMeasure<HTMLDivElement>();
  const contentHeight = contentDimensions.height;

  const handleToggle = () => {
    toggleOpen();
  };

  useEffect(
    () => {
      if (open !== isOpen) {
        handleToggle();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );

  return (
    <div data-testid="accordion" css={[defaultStyles.accordion]} data-is-open={isOpen}>
      <h1 css={defaultStyles.accordionTitleWrapper}>
        <TextIcon
          data-is-open={isOpen}
          css={[defaultStyles.accordionButton, buttonStyles]}
          aria-expanded={isOpen}
          aria-controls={sectionId}
          id={buttonId}
          onClick={handleToggle}
          icon={chevronUp}
          iconWidth={16}
          iconHeight={16}
        >
          <span css={[defaultStyles.accordionTitle, titleStyles]}>{title}</span>
        </TextIcon>
      </h1>
      <div
        style={{
          height: isOpen ? (contentHeight ? `${contentHeight + theme.spacing.xl}px` : 'auto') : 0,
        }}
        css={defaultStyles.accordionContentWrapper}
        id={sectionId}
        role="region"
        aria-labelledby={buttonId}
      >
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  );
};
